/**
 * A picture of the map (spec 026): the image when there is one and it has
 * decoded, else a dashed placeholder for a needed one, drawn by kind
 * (D-30): a person for a character, an object outline for a prop, a framed
 * landscape for a full picture or a background. A character's or a prop's
 * picture sits on a checkerboard (`checker`), so its transparency shows
 * (D-24).
 */
import React from 'react';
import type { PictureKind, SheetCut, SpriteSize } from '../../../../Shared/src/@types';
import { sheetFrameRect } from '../../../../Shared/src/utils/spriteSheet';
import styles from './Pictures.module.scss';

export interface PictureThumbProps {
  /** The image's object URL, once decoded. */
  src?: string;
  /** The picture exists (a filled slot or a state with an image). */
  filled: boolean;
  className?: string;
  testId?: string;
  /** Show the picture on a checkerboard (characters and props, D-24). */
  checker?: boolean;
  /** D-30: the kind a needed picture's placeholder draws (`data-placeholder-kind`); absent draws a person, unmarked. */
  placeholderKind?: PictureKind;
  /** Spec 035: a spritesheet shows one frame (`frame`, default the first), with the "MOVES" badge. */
  sheet?: { cut: SheetCut; natural?: SpriteSize; frame?: number };
  /** Drawn over the picture (the panel preview's play/pause button). */
  overlay?: React.ReactNode;
  /** Spec 035: false hides the "MOVES" badge (the panel preview, which has Play instead); cards keep it. */
  badge?: boolean;
}

/** One frame of a sheet (frame 0 unless playing), contained in its box. */
const FirstFrame: React.FC<{ src: string; cut: SheetCut; natural: SpriteSize; frame: number }> = ({ src, cut, natural, frame }) => {
  const r = sheetFrameRect(cut, frame, natural);
  const box = `${r.x} ${r.y} ${Math.max(1, r.w)} ${Math.max(1, r.h)}`;
  // The inner svg clips to the frame: a box wider or taller than the frame would show the next frames beside it.
  return (
    <svg viewBox={box} preserveAspectRatio="xMidYMid meet" data-testid="picture-first-frame" data-frame={frame} aria-hidden="true">
      <svg x={r.x} y={r.y} width={Math.max(1, r.w)} height={Math.max(1, r.h)} viewBox={box} overflow="hidden">
        <image href={src} width={natural.width} height={natural.height} style={cut.pixelArt ? { imageRendering: 'pixelated' } : undefined} />
      </svg>
    </svg>
  );
};

const Silhouette: React.FC = () => (
  <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
    <circle cx="20" cy="14" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M6 38c0-8 6-13 14-13s14 5 14 13" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

/** A prop: an object outline (a box with a lid). */
const ObjectOutline: React.FC = () => (
  <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
    <path d="M7 17h26v16H7z" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M9 17c0-6 5-9 11-9s11 3 11 9" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M18 21h4v4h-4z" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

/** A full picture or a background: a framed landscape. */
const Landscape: React.FC = () => (
  <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
    <path d="M4 9h32v22H4z" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M4 27l9-8 7 6 5-4 11 8" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="28" cy="15" r="2.5" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const Placeholder: React.FC<{ kind?: PictureKind }> = ({ kind }) =>
  (kind === 'prop' ? <ObjectOutline /> : kind === 'other' || kind === 'background' ? <Landscape /> : <Silhouette />);

export const PictureThumb: React.FC<PictureThumbProps> = ({ src, filled, className, testId, checker = false, sheet, placeholderKind, overlay, badge = true }) => {
  const onChecker = checker && filled;
  return (
  <span
    className={`${styles.thumb} ${filled ? '' : styles.silhouette} ${onChecker ? styles.checker : ''} ${className ?? ''}`}
    data-testid={testId}
    data-filled={filled ? 'true' : 'false'}
    data-checker={onChecker ? 'true' : undefined}
    data-placeholder-kind={!filled && placeholderKind ? placeholderKind : undefined}
  >
    {filled && src && sheet?.natural ? <FirstFrame src={src} cut={sheet.cut} natural={sheet.natural} frame={sheet.frame ?? 0} />
      : filled && src ? <img src={src} alt="" /> : filled ? null : <Placeholder kind={placeholderKind} />}
    {filled && sheet && badge && (
      <span className={styles.moves} data-testid="picture-moves"><span aria-hidden="true">▶</span> MOVES</span>
    )}
    {overlay}
  </span>
  );
};

export default PictureThumb;
